/*
 * Copyright (c) 2025 Sila Artificial Intelligence Research LLC
 * A DA Holding Ltd Company
 * All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 */

import { Auth0Client } from '@auth0/auth0-spa-js';

const domain = import.meta.env.VITE_AUTH0_DOMAIN;
const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
const audience = import.meta.env.VITE_AUTH0_AUDIENCE;

/**
 * Standalone Auth0 client for use outside React components (e.g., in loaders).
 * Uses the same config as Auth0ProviderWithNavigate.
 */
export const auth0Client = new Auth0Client({
  domain,
  clientId,
  authorizationParams: {
    redirect_uri: window.location.origin,
    audience,
    scope: 'openid profile email offline_access',
  },
  useRefreshTokens: true,
  cacheLocation: 'localstorage',
});

/**
 * Get access token for use in React Router loaders.
 * Returns the token if available, or null if not authenticated.
 */
export async function getAccessTokenForLoader(): Promise<string | null> {
  try {
    // Check if we're currently handling the callback
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.has('code') && searchParams.has('state')) {
      // Handle the callback first
      await auth0Client.handleRedirectCallback();
      // Clean up the URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    const token = await auth0Client.getTokenSilently();
    return token;
  } catch (error) {
    // Token not available - user not authenticated
    console.log('[Auth0 Loader] No token available:', error);
    return null;
  }
}

/**
 * Trigger login redirect. Call this when getAccessTokenForLoader returns null.
 */
export async function redirectToLogin(returnTo?: string): Promise<void> {
  await auth0Client.loginWithRedirect({
    appState: { returnTo: returnTo || window.location.pathname },
  });
}
